import React from 'react';
import Fade from '@mui/material/Fade';
import Slide from '@mui/material/Slide';
import Button from '@mui/material/Button';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CloseIcon from '@mui/icons-material/Close';
import { TransitionProps } from '@mui/material/transitions';
import SubmitButton from '@/components/SubmitButton';
import { useClintMediaQuery } from '@/utils/hooks';

const Transition = React.forwardRef(
  (
    props: TransitionProps & {
      children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
  ) => {
    const isMobile = useClintMediaQuery();
    const { className, children } = props;

    if (isMobile && className?.includes('w-full')) {
      return (
        <Slide direction="left" ref={ref} {...props}>
          {children}
        </Slide>
      );
    }
    return (
      <Fade ref={ref} {...props}>
        {children}
      </Fade>
    );
  },
);

interface UniversalModalProps extends Omit<DialogProps, 'title'> {
  footer?: React.ReactNode;
  width?: number | string;
  height?: number | string;
  title?: React.ReactNode;
  headerHeight?: number | string;
  confirmText?: string;
  deleteText?: string;
  onConfirm?: () => void;
  onDelete?: () => void;
  onClose?: (event?: any, reason?: string) => void;
  headerClass?: string;
  headerTitleClass?: string;
  contentRef?: React.MutableRefObject<HTMLDivElement | undefined>;
  closeIconColor?: string;
  defaultFooterDisabled?: boolean;
  submit?: boolean;
  handleClick?: () => void;
  headerBorder?: string;
}

const UniversalModal = (props: UniversalModalProps) => {
  const {
    open,
    children,
    title,
    footer,
    deleteText,
    width = 620,
    maxWidth,
    confirmText = '确定',
    height,
    headerHeight = 84,
    onClose,
    onConfirm,
    onDelete,
    contentRef,
    headerClass = '',
    headerTitleClass = '',
    PaperProps,
    closeIconColor = 'text-gray-400',
    defaultFooterDisabled = false,
    submit = false,
    handleClick,
    fullScreen = true,
    headerBorder = '1px solid #cbd5e1',
  } = props;
  const isMobile = useClintMediaQuery();

  const renderDefaultHeader = () => {
    return (
      <DialogTitle
        className={headerTitleClass}
        sx={{
          height: isMobile ? 70 : headerHeight,
          borderBottom: headerBorder,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          position: 'relative',
        }}
      >
        <div className={headerClass + ' w-full text-center'}>{title}</div>
        <IconButton
          className="!absolute !right-6 !top-1/2 !-translate-y-1/2 !p-1 !w-auto"
          onClick={onClose}
        >
          <CloseIcon className={closeIconColor} />
        </IconButton>
      </DialogTitle>
    );
  };

  const renderDefaultFooter = () => {
    return (
      <DialogActions className="!flex !items-center !justify-center !gap-2 !pt-3 !pb-6 !px-10 !border-t !border-t-gray-300 tablet:!px-2">
        {deleteText && (
          <Button
            variant="outlined"
            className="!shrink-0 !rounded-lg !h-full !border !border-gray-200 !px-8 !bg-white !text-black active:!border-gray-200 tablet:!hidden"
            onClick={onDelete}
          >
            {deleteText}
          </Button>
        )}
        <SubmitButton
          submit={submit}
          handleSubmit={onConfirm}
          handleClick={handleClick ? handleClick : onConfirm}
          disabled={defaultFooterDisabled}
          submitText={confirmText}
          className="!w-full !py-3 tablet:!max-w-[209px]"
        />
      </DialogActions>
    );
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth={maxWidth}
      fullScreen={fullScreen && isMobile}
      TransitionComponent={Transition}
      TransitionProps={{
        className: fullScreen && isMobile ? '!w-full' : '!w-auto',
      }}
      sx={
        isMobile
          ? undefined
          : {
              '.MuiDialog-paper': { width, height },
            }
      }
      className="!min-w-[370px] !min-h-[400px]"
      PaperProps={{
        ...PaperProps,
        className:
          PaperProps?.className ??
          '' +
            ` ${fullScreen ? '!shadow-none tablet:!rounded-lg' : '!rounded-lg'}`,
      }}
    >
      {title && renderDefaultHeader()}
      <DialogContent
        dividers={false}
        sx={{
          padding: 0,
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
        ref={contentRef}
      >
        {children}
      </DialogContent>
      {footer ? footer : renderDefaultFooter()}
    </Dialog>
  );
};

export default UniversalModal;
